import { localeDirection, preferredLocale } from '@/config/locales'
import React, { useEffect, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation, Outlet } from 'react-router-dom'
import { PageLoader } from '@/components/loading/PageLoader'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { refreshUserPermissions } from '@/redux/slices/authSlice'
import { setLanguage } from '@/redux/slices/languageSlice'
import { setRtl } from '@/redux/slices/configSlice'
import { setLocale } from '@/i18n'
import ErrorBoundary from '@/components/ErrorBoundary'
import RouteGuard from '@/components/RouteGuard'
import AppShell from '@/views/app/AppShell'
import SystemInfrastructureProvider from '@/components/providers/SystemInfrastructureProvider'
import ApiEventToaster from '@/components/providers/ApiEventToaster'
import { ThemeProvider } from '@/components/providers/ThemeProvider'
import { DirectionProvider } from '@radix-ui/react-direction'
import { Permission } from '@/enums/Permission';

// Lazy-loaded views (code-split per route)
const SignIn = React.lazy(() => import('@/views/app/sessions/SignIn'))
const Forgot = React.lazy(() => import('@/views/app/sessions/Forgot'))
const Reset = React.lazy(() => import('@/views/app/sessions/Reset'))
const RegisterTenant = React.lazy(() => import('@/views/app/sessions/RegisterTenant'))
const CreatingTenant = React.lazy(() => import('@/views/app/sessions/CreatingTenant'))
const AcceptInvitation = React.lazy(() => import('@/views/app/sessions/AcceptInvitation'))
const Dashboard = React.lazy(() => import('@/views/app/dashboard/DashboardContainer'))
const PosView = React.lazy(() => import('@/views/app/pages/Pos'))
const Profile = React.lazy(() => import('@/views/app/pages/profile'))
const NotFound = React.lazy(() => import('@/views/app/pages/notFound'))
const NotAuthorized = React.lazy(() => import('@/views/app/pages/NotAuthorize'))
const TenantNotFound = React.lazy(() => import('@/views/errors/TenantNotFound'))
const TenantExpired = React.lazy(() => import('@/views/errors/TenantExpired'))
const TenantSuspended = React.lazy(() => import('@/views/errors/TenantSuspended'))
const TenantDatabaseError = React.lazy(() => import('@/views/errors/TenantDatabaseError'))

// Module layouts / list pages — each module's index.tsx is its tab container
const SalesPage = React.lazy(() => import('@/views/app/pages/sales'))
const PurchasesPage = React.lazy(() => import('@/views/app/pages/purchases'))
const ProductsPage = React.lazy(() => import('@/views/app/pages/products'))
const ProductInventoryTransactions = React.lazy(() => import('@/views/app/pages/products/product_inventory_transactions'))
const AccountsPage = React.lazy(() => import('@/views/app/pages/accounts'))
const AdjustmentPage = React.lazy(() => import('@/views/app/pages/adjustment'))
const TransfersPage = React.lazy(() => import('@/views/app/pages/transfers'))
const PeoplePage = React.lazy(() => import('@/views/app/pages/people'))
const HrmPage = React.lazy(() => import('@/views/app/pages/hrm'))
const PayrollPage = React.lazy(() => import('@/views/app/pages/hrm/index_payroll'))
const SettingsPage = React.lazy(() => import('@/views/app/pages/settings'))
const TenantUsersPage = React.lazy(() => import('@/views/app/pages/settings/tenant/users'))
const TenantUserInvitePage = React.lazy(() => import('@/views/app/pages/settings/tenant/users/invite'))
const ReportsPage = React.lazy(() => import('@/views/app/pages/reports'))
const QuotationsPage = React.lazy(() => import('@/views/app/pages/quotations'))
const ReceiptsPage = React.lazy(() => import('@/views/app/pages/receipts'))
const ProjectsPage = React.lazy(() => import('@/views/app/pages/projects'))
const ProjectsListPage = React.lazy(() => import('@/views/app/pages/projects/index_project'))
const ProjectsCreatePage = React.lazy(() => import('@/views/app/pages/projects/create_project'))
const ProjectsEditPage = React.lazy(() => import('@/views/app/pages/projects/Edit_project'))
const TasksPage = React.lazy(() => import('@/views/app/pages/tasks'))
const TasksListPage = React.lazy(() => import('@/views/app/pages/tasks/index_task'))
const TasksCreatePage = React.lazy(() => import('@/views/app/pages/tasks/create_task'))
const TasksEditPage = React.lazy(() => import('@/views/app/pages/tasks/Edit_task'))
const FixedAssetsPage = React.lazy(() => import('@/views/app/pages/fixed-assets'))
const OpeningInventoryPage = React.lazy(() => import('@/views/app/pages/opening_inventory'))
const PersonalWithdrawalsPage = React.lazy(() => import('@/views/app/pages/personal_withdrawals'))
const FinancialStatementsPage = React.lazy(() => import('@/views/app/pages/financial_statements'))
const AiQueryPage = React.lazy(() => import('@/views/app/pages/ai-query'))
const FilesPage = React.lazy(() => import('@/views/app/pages/files'))
const OnboardingPage = React.lazy(() => import('@/views/app/pages/onboarding'))
const BotGatewayPage = React.lazy(() => import('@/views/app/pages/bot-gateway'))

// Standalone Accounting Pages
const IntegrityCheck = React.lazy(() => import('@/views/app/pages/accounting/integrity-check'))
const MyApprovals = React.lazy(() => import('@/views/app/pages/hrm/my-approvals'))
const GlPostingQueue = React.lazy(() => import('@/views/app/pages/accounting/gl-posting-queue'))
const AdjustingEntryWizard = React.lazy(() => import('@/views/app/pages/accounting/adjusting-entry-quick-form'))
const JournalDetail = React.lazy(() => import('@/views/app/pages/accounting/journal-detail'))
const GlSearch = React.lazy(() => import('@/views/app/pages/accounting/gl-search'))
const JournalApprovals = React.lazy(() => import('@/views/app/pages/accounting/journal-approvals'))
const OperationalFinance = React.lazy(() => import('@/views/app/pages/accounting/OperationalFinance'))
const GroupFinance = React.lazy(() => import('@/views/app/pages/accounting/GroupFinance'))
const LedgerExtensions = React.lazy(() => import('@/views/app/pages/accounting/LedgerExtensions'))
const CompanyTaxProfiles = React.lazy(() => import('@/views/app/pages/accounting/CompanyTaxProfiles'))
const TaxWorkingPaper = React.lazy(() => import('@/views/app/pages/accounting/TaxWorkingPaper'))
const IssuedOperationalDocuments = React.lazy(() => import('@/views/app/pages/accounting/IssuedOperationalDocuments'))
const IssuedSaleDocuments = React.lazy(() => import('@/views/app/pages/accounting/IssuedSaleDocuments'))
const EnterpriseDeliveryHealth = React.lazy(() => import('@/views/app/pages/accounting/EnterpriseDeliveryHealth'))
const PostClosingVerification = React.lazy(() => import('@/views/app/pages/accounting/PostClosingVerification'))
const ComplianceDashboard = React.lazy(() => import('@/views/app/pages/accounting/ComplianceDashboard'))
const OpeningBalances = React.lazy(() => import('@/views/app/pages/accounting/OpeningBalances'))
const OpeningBalanceCarryForward = React.lazy(() => import('@/views/app/pages/accounting/OpeningBalanceCarryForward'))
const AccountLedger = React.lazy(() => import('@/views/app/pages/accounts/account-ledger'))
const Budgeting = React.lazy(() => import('@/views/app/pages/accounting/budgeting'))
const BudgetDetail = React.lazy(() => import('@/views/app/pages/accounting/budget-detail'))
const RecurringJournals = React.lazy(() => import('@/views/app/pages/accounting/RecurringJournals'))
const ReconciliationDashboard = React.lazy(() => import('@/views/app/pages/accounting/ReconciliationDashboard'))

// New standalone pages — wired to previously orphaned backend controllers
const Leases = React.lazy(() => import('@/views/app/pages/accounting/PhaseFive').then(m => ({ default: m.Leases })));
const BookDepreciation = React.lazy(() => import('@/views/app/pages/accounting/PhaseFive').then(m => ({ default: m.BookDepreciation })));
const InventoryCosting = React.lazy(() => import('@/views/app/pages/accounting/InventoryCosting'))
const DeferredRevenue = React.lazy(() => import('@/views/app/pages/accounting/DeferredRevenue'))
const AccruedRevenue = React.lazy(() => import('@/views/app/pages/accounting/AccruedRevenue'))
const PrepaidExpenses = React.lazy(() => import('@/views/app/pages/accounting/PrepaidExpenses'))
const CurrencyRevaluation = React.lazy(() => import('@/views/app/pages/accounting/CurrencyRevaluation'))
const CustodyPayments = React.lazy(() => import('@/views/app/pages/accounting/CustodyPayments'))
const Collections = React.lazy(() => import('@/views/app/pages/accounting/Collections'))
const LandedCosts = React.lazy(() => import('@/views/app/pages/purchases/LandedCosts'))
const BatchJourney = React.lazy(() => import('@/views/app/pages/products/BatchJourney'))
const Replenishment = React.lazy(() => import('@/views/app/pages/products/Replenishment'))
const CommercialPolicies = React.lazy(() => import('@/views/app/pages/accounting/CommercialPolicies'))
const MasterImportWorkbench = React.lazy(() => import('@/views/app/pages/accounts/MasterImportWorkbench'))
const AccountImportExport = React.lazy(() => import('@/views/app/pages/accounts/AccountImportExport'))
const ExpenseDepositCategories = React.lazy(() => import('@/views/app/pages/settings/ExpenseDepositCategories'))
const ShipmentTracking = React.lazy(() => import('@/views/app/pages/sales/ShipmentTracking'))
const DynamicReports = React.lazy(() => import('@/views/app/pages/reports/DynamicReports'))
const TaxBrackets = React.lazy(() => import('@/views/app/pages/hrm/TaxBrackets'))
const LoanSettlement = React.lazy(() => import('@/views/app/pages/hrm/LoanSettlement'))
const PartialDisbursement = React.lazy(() => import('@/views/app/pages/hrm/PartialDisbursement'))

// GL financial statements (cost-center scoped) — F0/G-06 wiring

// Payroll run dashboard — F0/G-06 wiring (linked from payroll-periods.tsx)
const PayrollRunDashboard = React.lazy(() => import('@/views/app/pages/hrm/payroll-run'))

// Admin module (super-admin only) — separate layout, separate guard
const AdminLayout = React.lazy(() =>
  import('@/layouts/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const AdminDashboard = React.lazy(() => import('@/views/admin/AdminDashboard'))
const AdminUserManagement = React.lazy(() => import('@/views/admin/UserManagement'))
const AdminTenantManagement = React.lazy(() => import('@/views/admin/TenantManagement'))
const AdminRoleManagement = React.lazy(() => import('@/views/admin/RoleManagement'))
const AdminPlanManagement = React.lazy(() => import('@/views/admin/PlanManagement'))
const AdminSettings = React.lazy(() => import('@/views/admin/Settings'))
const AdminModules = React.lazy(() => import('@/views/admin/Modules'))
const AdminBackup = React.lazy(() => import('@/views/admin/Backup'))
const AdminLogs = React.lazy(() => import('@/views/admin/Logs'))
const AdminNotifications = React.lazy(() => import('@/views/admin/Notifications'))
const AdminReportManagement = React.lazy(() => import('@/views/admin/ReportManagement'))
const AdminSystemResources = React.lazy(() => import('@/views/admin/SystemResources'))



// Wrapper to extract token from URL query/path and pass to Reset
const ResetWithToken: React.FC = () => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const pathSegments = location.pathname.split('/')
  const lastSegment = pathSegments[pathSegments.length - 1]
  // `/reset-password` has no token segment — without this guard the literal
  // route name is forwarded to Reset as if it were a reset token.
  const pathToken = lastSegment && lastSegment !== 'reset-password' ? lastSegment : ''
  const token = searchParams.get('token') || pathToken
  return <Reset token={token} />
}

export const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const isRtl = useAppSelector((state) => state.config.themeMode.rtl)

  useEffect(() => {
    const loadingWrap = document.getElementById('loading_wrap')
    if (loadingWrap) {
      loadingWrap.style.display = 'none'
    }
  }, [])

  useEffect(() => {
    // Revalidate persisted grants on every restored session. Keep cached
    // grants usable offline; transient refresh failures do not clear them.
    if (isAuthenticated) {
      dispatch(refreshUserPermissions() as any)
    }
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    // Sync dir attribute with persisted language on app load
    const savedLanguage = preferredLocale()
    document.documentElement.dir = localeDirection(savedLanguage)
    document.documentElement.lang = savedLanguage
    
    dispatch(setLanguage(savedLanguage))
    dispatch(setRtl(localeDirection(savedLanguage) === 'rtl'))
    setLocale(savedLanguage)
  }, [dispatch])

  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr'
    if (isRtl) {
      document.body.classList.add('rtl')
    } else {
      document.body.classList.remove('rtl')
    }
  }, [isRtl])

  return (
    <DirectionProvider dir={isRtl ? 'rtl' : 'ltr'}>
      <BrowserRouter>
        <ThemeProvider>
        <SystemInfrastructureProvider>
          <ApiEventToaster />
          <ErrorBoundary>
          <Routes>
            <Route element={<Suspense fallback={<PageLoader />}><Outlet /></Suspense>}>
              {/* Public Routes — error pages & auth */}
              <Route path="/error/tenant-not-found" element={<TenantNotFound />} />
              <Route path="/tenant-expired" element={<TenantExpired />} />
              <Route path="/tenant-suspended" element={<TenantSuspended />} />
              <Route path="/tenant-database-error" element={<TenantDatabaseError />} />
              <Route path="/tenant-not-found" element={<TenantNotFound />} />

              <Route path="/login" element={<SignIn />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/register" element={<RegisterTenant />} />
              <Route path="/creating-tenant" element={<CreatingTenant />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route
                path="/reset-password"
                element={<ResetWithToken />}
              />
              <Route
                path="/reset-password/:token"
                element={<ResetWithToken />}
              />
            </Route>

            {/* Auth Required App Shell — sidebar + topbar + outlet */}
            <Route element={<RouteGuard authRequired />}>
              <Route element={<AppShell />}>
                <Route path="/" element={<Navigate to="/app/dashboard" replace />} />

                  {/* Dashboard */}
                  <Route path="/app/dashboard" element={<Dashboard />} />

                  {/* Profile (no permission required) */}
                  <Route path="/app/profile" element={<Profile />} />

                  {/* Sales */}
                  <Route element={<RouteGuard permission={Permission.SALES_VIEW} />}>
                    <Route path="/app/sales" element={<SalesPage />} />
                    <Route path="/app/sales/shipments" element={<ShipmentTracking />} />
                    <Route path="/app/sales/*" element={<SalesPage />} />
                  </Route>

                  {/* Quotations */}
                  <Route element={<RouteGuard permission={Permission.QUOTATIONS_VIEW} />}>
                    <Route path="/app/quotations" element={<QuotationsPage />} />
                  </Route>

                  {/* POS — fullscreen, no AppShell sidebar/navbar (moved outside AppShell) */}

                  {/* Purchases */}
                  <Route element={<RouteGuard permission={Permission.PURCHASES_VIEW} />}>
                    <Route path="/app/purchases" element={<PurchasesPage />} />
                    <Route path="/app/purchases/*" element={<PurchasesPage />} />
                  </Route>

                  {/* Products / Inventory */}
                  <Route element={<RouteGuard permission={Permission.PRODUCTS_VIEW} />}>
                    <Route path="/app/products" element={<ProductsPage />} />
                    <Route path="/app/products/transactions/:id" element={<ProductInventoryTransactions />} />
                    <Route path="/app/products/*" element={<ProductsPage />} />
                  </Route>

                  {/* Adjustments */}
                  <Route element={<RouteGuard permission={Permission.ADJUSTMENT_VIEW} />}>
                    <Route path="/app/adjustments" element={<AdjustmentPage />} />
                  </Route>

                  {/* Transfers */}
                  <Route element={<RouteGuard permission={Permission.TRANSFER_VIEW} />}>
                    <Route path="/app/transfers" element={<TransfersPage />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.REPORTS_FINANCIAL} />}>
                    <Route path="/app/accounting/operational-finance" element={<OperationalFinance />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.GROUP_FINANCE_VIEW} />}>
                    <Route path="/app/accounting/group-finance" element={<GroupFinance />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.BOOKS_VIEW} />}>
                    <Route path="/app/accounting/book-depreciation" element={<BookDepreciation />} />
                    <Route path="/app/accounting/books" element={<LedgerExtensions family="books" />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.INTERCOMPANY_VIEW} />}>
                    <Route path="/app/accounting/intercompany" element={<LedgerExtensions family="intercompany" />} />
                  </Route>
                  {/* Accounts / Accounting */}
                  <Route element={<RouteGuard permission={Permission.PURCHASES_VIEW} />}>
                    <Route path="/app/accounting/issued-purchase" element={<IssuedOperationalDocuments kind="purchase" />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.SALE_RETURNS_VIEW} />}>
                    <Route path="/app/accounting/issued-sale_return" element={<IssuedOperationalDocuments kind="sale_return" />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.PURCHASE_RETURNS_VIEW} />}>
                    <Route path="/app/accounting/issued-purchase_return" element={<IssuedOperationalDocuments kind="purchase_return" />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.SALES_VIEW} />}>
                    <Route path="/app/accounting/issued-sales" element={<IssuedSaleDocuments />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.REPORTS_FINANCIAL} />}>
                    <Route path="/app/accounting/tax-working-paper" element={<TaxWorkingPaper />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.ACCOUNTING_SETTINGS} />}>
                    <Route path="/app/accounting/inventory-costing" element={<InventoryCosting />} />
                    <Route path="/app/accounting/company-tax-profiles" element={<CompanyTaxProfiles />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.AUDITLOG_VIEW} />}>
                    <Route path="/app/accounting/delivery-health" element={<EnterpriseDeliveryHealth />} />
                  </Route>
                  <Route element={<RouteGuard permission={Permission.ACCOUNT} />}>
                    <Route path="/app/accounts" element={<AccountsPage />} />
                    <Route path="/app/accounts/*" element={<AccountsPage />} />

                    {/* Standalone Accounting Pages */}
                    <Route path="/app/accounting/budgets" element={<Budgeting />} />
                    <Route path="/app/accounting/budgets/:id" element={<BudgetDetail />} />
                    <Route path="/app/accounting/integrity-check" element={<IntegrityCheck />} />
                    <Route path="/app/accounting/gl-posting-queue" element={<GlPostingQueue />} />
                    <Route path="/app/accounting/adjusting-entry-wizard" element={<AdjustingEntryWizard />} />
                    <Route path="/app/accounting/journal-detail/:id" element={<JournalDetail />} />
                    <Route path="/app/accounting/gl-search" element={<GlSearch />} />
                    <Route path="/app/accounting/journal-approvals" element={<JournalApprovals />} />
                    <Route path="/app/accounting/recurring-journals" element={<RecurringJournals />} />
                    <Route path="/app/accounting/post-closing-verification" element={<PostClosingVerification />} />
                    <Route path="/app/accounting/compliance-dashboard" element={<ComplianceDashboard />} />
                    <Route path="/app/accounting/reconciliation-dashboard" element={<ReconciliationDashboard />} />
                    <Route path="/app/accounting/opening-balances" element={<OpeningBalances />} />
                    <Route path="/app/accounting/opening-balance-carry-forward" element={<OpeningBalanceCarryForward />} />
                    <Route path="/app/accounting/account-ledger/:accountId" element={<Suspense fallback={<PageLoader />}><AccountLedger /></Suspense>} />
                    <Route element={<RouteGuard permission={Permission.REPORTS_FINANCIAL} />}><Route path="/app/accounting/leases" element={<Leases />} /><Route path="/app/accounting/deferred-revenue" element={<DeferredRevenue />} /></Route>
                    <Route path="/app/accounting/accrued-revenue" element={<AccruedRevenue />} />
                    <Route path="/app/accounting/prepaid-expenses" element={<PrepaidExpenses />} />
                    <Route path="/app/accounting/currency-revaluation" element={<CurrencyRevaluation />} />
                    <Route path="/app/accounting/custody-payments" element={<CustodyPayments />} />
                    <Route path="/app/accounts/import-export" element={<AccountImportExport />} />
                  </Route>

                  {/* Fixed Assets */}
                  <Route element={<RouteGuard permission={Permission.FIXED_ASSET_VIEW} />}>
                    <Route path="/app/fixed-assets" element={<FixedAssetsPage />} />
                    <Route path="/app/fixed-assets/*" element={<FixedAssetsPage />} />
                  </Route>

                  {/* Personal Withdrawals */}
                  <Route element={<RouteGuard permission={Permission.PERSONAL_WITHDRAWAL_VIEW} />}>
                    <Route path="/app/personal_withdrawals" element={<PersonalWithdrawalsPage />} />
                  </Route>

                  {/* HRM */}
                  <Route element={<RouteGuard permission={Permission.VIEW_EMPLOYEE} />}>
                    <Route path="/app/hrm" element={<HrmPage />} />
                    <Route path="/app/hrm/*" element={<HrmPage />} />
                    <Route path="/app/hrm/tax-brackets" element={<TaxBrackets />} />
                    <Route path="/app/hrm/loan-settlement" element={<LoanSettlement />} />
                    <Route path="/app/hrm/partial-disbursement" element={<PartialDisbursement />} />
                  </Route>

                  {/* Payroll */}
                  <Route element={<RouteGuard permission={Permission.PAYROLL} />}>
                    <Route path="/app/payroll" element={<PayrollPage />} />
                    <Route path="/app/payroll/*" element={<PayrollPage />} />
                    <Route path="/app/hrm/payroll-run/:id" element={<PayrollRunDashboard />} />
                  </Route>

                  {/* Payroll approval inbox — gated by the approval permission
                      (menu.ts), not the accounting permission it previously
                      sat behind, which locked out pure payroll approvers. */}
                  <Route element={<RouteGuard permission={[Permission.PAYROLL_RUN_APPROVE, Permission.JOURNAL_APPROVE, Permission.ACCOUNTING_SETTINGS, Permission.ACCOUNT, Permission.CUSTOMERS_ADD, Permission.CUSTOMERS_EDIT, Permission.SUPPLIERS_ADD, Permission.SUPPLIERS_EDIT, Permission.PRODUCTS_ADD, Permission.PRODUCTS_EDIT]} />}>
                    <Route path="/app/hrm/my-approvals" element={<MyApprovals />} />
                  </Route>

                  <Route element={<RouteGuard permission={[Permission.ACCOUNT, Permission.CUSTOMERS_ADD, Permission.CUSTOMERS_EDIT, Permission.SUPPLIERS_ADD, Permission.SUPPLIERS_EDIT, Permission.PRODUCTS_ADD, Permission.PRODUCTS_EDIT]} />}>
                    <Route path="/app/master-imports" element={<MasterImportWorkbench />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.ACCOUNTING_SETTINGS} />}>
                    <Route path="/app/accounting/commercial-policies" element={<CommercialPolicies />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.REPORTS_FINANCIAL} />}>
                    <Route path="/app/accounting/collections" element={<Collections />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.PURCHASES_EDIT} />}>
                    <Route path="/app/purchases/landed-costs" element={<LandedCosts />} />
                  </Route>

                  <Route element={<RouteGuard permission={Permission.STOCK_REPORT} />}>
                    <Route path="/app/inventory/batch-journey" element={<BatchJourney />} />
                    <Route path="/app/replenishment" element={<Replenishment />} />
                  </Route>

                  {/* People */}
                  <Route element={<RouteGuard permission={[Permission.CUSTOMERS_VIEW, Permission.SUPPLIERS_VIEW, Permission.USERS_VIEW]} />}>
                    <Route path="/app/people" element={<PeoplePage />} />
                    <Route path="/app/people/*" element={<PeoplePage />} />
                  </Route>

                  {/* Reports */}
                  <Route element={<RouteGuard permission={[
                    Permission.REPORTS_SALES,
                    Permission.REPORTS_PURCHASE,
                    Permission.REPORTS_CUSTOMERS,
                    Permission.REPORTS_SUPPLIERS,
                    Permission.REPORTS_PROFIT,
                    Permission.REPORTS_QUANTITY_ALERTS,
                    Permission.WAREHOUSE_REPORT,
                    Permission.REPORTS_PAYMENTS_SALES,
                    Permission.REPORTS_PAYMENTS_PURCHASES,
                    Permission.REPORTS_PAYMENTS_SALE_RETURNS,
                    Permission.REPORTS_PAYMENTS_PURCHASE_RETURN,
                    Permission.REPORTS_FINANCIAL,
                  ]} />}>
                    <Route path="/app/reports" element={<ReportsPage />} />
                    <Route path="/app/reports/dynamic" element={<DynamicReports />} />
                    <Route path="/app/reports/*" element={<ReportsPage />} />
                  </Route>

                  {/* Receipts */}
                  <Route element={<RouteGuard permission={Permission.PAYMENT_VIEW_ALL} />}>
                    <Route path="/app/receipts" element={<ReceiptsPage />} />
                    <Route path="/app/receipts/*" element={<ReceiptsPage />} />
                  </Route>

                  {/* Projects */}
                  <Route element={<RouteGuard permission={Permission.PROJECTS} />}>
                    <Route path="/app/projects" element={<ProjectsPage />}>
                      <Route index element={<ProjectsListPage />} />
                      <Route path="store" element={<ProjectsCreatePage />} />
                      <Route path="edit/:id" element={<ProjectsEditPage />} />
                    </Route>
                  </Route>

                  {/* Tasks */}
                  <Route element={<RouteGuard permission={Permission.TASKS} />}>
                    <Route path="/app/tasks" element={<TasksPage />}>
                      <Route index element={<TasksListPage />} />
                      <Route path="store" element={<TasksCreatePage />} />
                      <Route path="edit/:id" element={<TasksEditPage />} />
                    </Route>
                  </Route>

                  {/* Settings */}
                  <Route element={<RouteGuard permission={Permission.SETTING_SYSTEM} />}>
                    <Route path="/app/settings" element={<SettingsPage />} />
                    <Route path="/app/settings/*" element={<SettingsPage />} />
                    <Route path="/app/settings/expense-deposit-categories" element={<ExpenseDepositCategories />} />
                  </Route>

                  {/* Tenant user management */}
                  <Route element={<RouteGuard permission={Permission.USERS_VIEW} />}>
                    <Route path="/app/settings/tenant/users" element={<TenantUsersPage />} />
                    <Route path="/app/settings/tenant/users/invite" element={<TenantUserInvitePage />} />
                  </Route>

                  {/* Opening Inventory */}
                  <Route element={<RouteGuard permission={Permission.SETUP_OPENING_INVENTORY} />}>
                    <Route path="/app/opening_inventory" element={<OpeningInventoryPage />} />
                    <Route path="/app/opening_inventory/*" element={<OpeningInventoryPage />} />
                  </Route>

                  {/* Financial Statements */}
                  <Route element={<RouteGuard permission={Permission.REPORTS_FINANCIAL} />}>
                    <Route path="/app/financial_statements" element={<FinancialStatementsPage />} />
                    <Route path="/app/financial_statements/*" element={<FinancialStatementsPage />} />
                    <Route path="/app/financial_statements/gl-income-statement" element={<FinancialStatementsPage initialTab="income_statement" />} />
                    <Route path="/app/financial_statements/gl-balance-sheet" element={<FinancialStatementsPage initialTab="balance_sheet" />} />
                  </Route>

                  {/* Files */}
                  <Route element={<RouteGuard permission={Permission.SETTING_SYSTEM} />}>
                    <Route path="/app/files" element={<FilesPage />} />
                    <Route path="/app/files/*" element={<FilesPage />} />
                  </Route>

                  {/* AI Query */}
                  <Route element={<RouteGuard permission={Permission.REPORTS_VIEW_AI} />}>
                    <Route path="/app/ai-query" element={<AiQueryPage />} />
                  </Route>

                  {/* Bot Gateway (WhatsApp & Telegram) */}
                  <Route element={<RouteGuard permission={[Permission.BOT_CHANNELS_MANAGE, Permission.BOT_LIVE_CHAT_USE, Permission.BOT_TEMPLATES_MANAGE, Permission.BOT_AUTO_REPLIES_MANAGE]} />}>
                    <Route path="/app/bot-gateway" element={<BotGatewayPage />} />
                    <Route path="/app/bot-gateway/*" element={<BotGatewayPage />} />
                  </Route>

                  {/* Onboarding (chrome hidden — see LargeSidebarLayout) */}
                  <Route path="/app/onboarding/*" element={<OnboardingPage />} />

                  {/* App-level errors */}
                  <Route path="/not-authorized" element={<NotAuthorized />} />
                  <Route path="/notFound" element={<NotFound />} />
                </Route>
              </Route>

              {/* POS — fullscreen, no AppShell. Requires BOTH posView (menu
              // visibility) AND salesPos (backend create_pos gate in
              // PosRequest/SalePolicy) — nested guards are AND semantics. */}
            <Route element={<RouteGuard authRequired />}>
              <Route element={<RouteGuard permission={Permission.POS_VIEW} />}>
              <Route element={<RouteGuard permission={Permission.SALES_POS} />}>
                <Route path="/app/pos" element={<Suspense fallback={<PageLoader />}><PosView /></Suspense>} />
              </Route>
              </Route>
            </Route>

              {/* Admin module — super-admin only, separate layout + separate API surface */}
            <Route element={<RouteGuard authRequired />}>
              <Route element={<RouteGuard requiresSuperAdmin />}>
                <Route element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
                    <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/users" element={<AdminUserManagement />} />
                    <Route path="/admin/tenants" element={<AdminTenantManagement />} />
                    <Route path="/admin/roles" element={<AdminRoleManagement />} />
                    <Route path="/admin/plans" element={<AdminPlanManagement />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />
                    <Route path="/admin/modules" element={<AdminModules />} />
                    <Route path="/admin/backup" element={<AdminBackup />} />
                    <Route path="/admin/logs" element={<AdminLogs />} />
                    <Route path="/admin/notifications" element={<AdminNotifications />} />
                    <Route path="/admin/reports" element={<AdminReportManagement />} />
                    <Route path="/admin/system" element={<AdminSystemResources />} />
                  </Route>
                </Route>
              </Route>

            {/* Fallback — render NotFound page for any unmatched URL.
                Authenticated users see a 404 page inside the SPA;
                explicit index redirects above handle known entry points. */}
            <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
          </Routes>
        </ErrorBoundary>
      </SystemInfrastructureProvider>
      </ThemeProvider>
    </BrowserRouter>
    </DirectionProvider>
  )
}

export default App

